import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { normalizedEmailFilter, normalizeEmail } from '@nsc/smartteams-shared/api/email-filter.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

/**
 * Migrates a field engineer's email address to a new one.
 * Updates both the field engineer record and the linked user account (if exists).
 */
export const migrateFieldEngineerEmail = registerMutation({
  operationName: 'migrateFieldEngineerEmail',
  type: () => Boolean,
  params: [
    { name: 'fieldEngineerId', type: () => String },
    { name: 'newEmail', type: () => String },
  ],
  resolve: (fieldEngineerId: string, newEmail: string) => async () => {
    const hasPermission = await accessController.can(createPermissionPayload(SmartTeamsPermission.FEUpdate));

    // Check permission to modify field engineers
    if (!hasPermission) {
      throw new Error('No permission to modify field engineers');
    }

    // Fetch the field engineer
    const fieldEngineer = await getByID(SmartTeamsFieldEngineer, fieldEngineerId)({ _id: 1, email: 1, userId: 1 });

    if (!fieldEngineer) {
      throw new Error('Field engineer not found');
    }

    // Persist the email in its canonical (lowercase) form — this path writes raw, bypassing the
    // entity hooks that would otherwise normalize + enforce uniqueness.
    const normalizedNewEmail = normalizeEmail(newEmail);

    // Validate new email is different (case-insensitive)
    if (fieldEngineer.email && normalizeEmail(fieldEngineer.email) === normalizedNewEmail) {
      throw new ExpectedError('New email is the same as current email');
    }

    const feCollection = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
      SmartTeamsFieldEngineer._collectionName,
    );
    const userCollection = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);

    // Reject if another field engineer already uses this email
    const duplicateFieldEngineer = await feCollection.findOne(
      { ...normalizedEmailFilter(normalizedNewEmail), _id: { $ne: fieldEngineerId } },
      { projection: { _id: 1 } },
    );
    if (duplicateFieldEngineer) {
      throw new ExpectedError(`A field engineer with email ${normalizedNewEmail} already exists.`, {
        translationKey: 'field_engineer.email_in_use',
      });
    }

    // Reject if the email is already taken by a different user account
    const duplicateUser = await userCollection.findOne(
      {
        ...normalizedEmailFilter(normalizedNewEmail),
        ...(fieldEngineer.userId ? { _id: { $ne: fieldEngineer.userId } } : {}),
      },
      { projection: { _id: 1 } },
    );
    if (duplicateUser) {
      throw new ExpectedError(`Email ${normalizedNewEmail} is already in use.`, {
        translationKey: 'users.email_in_use',
      });
    }

    // Update field engineer email
    await feCollection.updateOne({ _id: fieldEngineerId }, { $set: { email: normalizedNewEmail } });

    // Update linked user if exists
    if (fieldEngineer.userId) {
      await userCollection.updateOne(
        { _id: fieldEngineer.userId },
        { $set: { username: normalizedNewEmail, email: normalizedNewEmail } },
      );
    }
    return true;
  },
});
